// When something happened, in words (gift letter, 2026-10-19).
//
// One rule: live surfaces (the sent page) say it relative to now under a week
// ("2 hours ago", "yesterday", "3 days ago"), then the date ("on Oct 23"). The
// Journal is the archive and always says the exact day and time
// ("Sun, Oct 19 · 2:14 pm"). Everything takes `now` so tests pin it.

const DAY = 24 * 60 * 60 * 1000

function toDate (iso) {
  if (!iso) return null
  const d = iso instanceof Date ? iso : new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

function startOfDay (d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

// Whole calendar days from `a` to `b` (local time): yesterday is 1.
function calendarDays (a, b) {
  return Math.round((startOfDay(b) - startOfDay(a)) / DAY)
}

// "Oct 19"
export function shortDate (iso) {
  const d = toDate(iso)
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''
}

// "2:14 pm"
function clock (d) {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/\s?([AP])M$/i, (m, p) => ` ${p.toLowerCase()}m`)
}

// "Sun, Oct 19 · 2:14 pm" (the Journal's exact time)
export function exactTime (iso) {
  const d = toDate(iso)
  if (!d) return ''
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  return `${day} · ${clock(d)}`
}

// "a moment ago", "5 minutes ago", "an hour ago", "3 hours ago",
// "yesterday", "3 days ago", then "on Oct 23". Reads after a verb:
// "Opened 2 hours ago", "Taken back on Oct 23".
export function relativeTime (iso, now = new Date()) {
  const d = toDate(iso)
  if (!d) return ''
  const ms = now - d
  if (ms < 60 * 1000) return 'a moment ago'
  const minutes = Math.floor(ms / 60000)
  if (minutes < 60) return minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`
  const days = calendarDays(d, now)
  const hours = Math.floor(minutes / 60)
  if (days === 0 || hours < 6) return hours === 1 ? 'an hour ago' : `${hours} hours ago`
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  return `on ${shortDate(d)}`
}

// When a reminder at `iso` will go, as a time of day the sender can picture:
// "this morning", "tomorrow morning", "on Wednesday morning", "on Oct 23".
// (Reminders go at 10am in the sender's zone.)
export function morningOf (iso, now = new Date()) {
  const d = toDate(iso)
  if (!d) return ''
  const days = calendarDays(now, d)
  const part = d.getHours() < 12 ? 'morning' : d.getHours() < 17 ? 'afternoon' : 'evening'
  if (days <= 0) return `this ${part}`
  if (days === 1) return `tomorrow ${part}`
  if (days < 7) return `on ${d.toLocaleDateString('en-US', { weekday: 'long' })} ${part}`
  return `on ${shortDate(d)}`
}
